import * as authActions from 'src/app/core/actions/auth.actions';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { saveObjectOutputSettingsStateSuccess } from '../actions/object.actions';
import { DataObject, OutputSettingsType } from '../models/core.models';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import {
  OutputSettings,
  OutputTemplate,
} from '../models/output-settings.models';

import { OutputSettingsApiService } from 'src/app/api/services/output-settings-api.service';
import {
  TemplateResponse,
  UtilsDataObjectApiService,
} from 'src/app/api/services/utils-data-object-api.service';
import { WidgetApiService } from 'src/app/api/services/widget-api.service';

import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
/**
 * Service responsible for managing output settings.
 */
export class OutputSettingsService {
  private templates$: Observable<TemplateResponse>;
  protected translateService = inject(TranslateService);

  private outputSettingsApi = inject(OutputSettingsApiService);
  private utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private widgetApi = inject(WidgetApiService);

  private destroyRef = inject(DestroyRef);
  constructor(
    private actions$: Actions,
    private store: Store,
  ) {
    // subscribe for global context-changing events to clear the static caches stored in the service
    this.actions$
      .pipe(
        ofType(authActions.logout, authActions.switchUserCompany),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.clearCache());
  }

  /** single, cached HTTP request */
  private loadAllTemplates(): Observable<TemplateResponse> {
    if (!this.templates$) {
      this.templates$ = this.utilsDataObjectApi.getOutputTemplates().pipe(
        // cache the latest successful response for all future subscribers
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }
    return this.templates$;
  }

  /** still returns an Observable of only the desired format */
  getOutputTemplatesForFormat(format: number): Observable<OutputTemplate[]> {
    return this.loadAllTemplates().pipe(
      map((response) => {
        const templates = response[format.toString()] || [];
        const emptyTemplate: OutputTemplate = {
          id: 0,
          file_path: '',
          language_id: 0,
          name: this.translateService.instant(
            'output-settings-editor.label.no-template',
          ),
        };
        return [emptyTemplate, ...templates];
      }),
    );
  }

  /** if you ever want to force a reload: */
  clearCache() {
    // console.log('output-settings-service: clear caches');
    this.templates$ = null;
  }

  fetchWidgetOutputFormatsSettings(
    widgetId: string,
  ): Observable<OutputSettings> {
    return this.widgetApi.getWidgetOutputFormatsSettings(widgetId);
  }

  fetchObjectOutputSettings(
    objectId: string,
    objectClassId: number,
  ): Observable<OutputSettings> {
    return this.outputSettingsApi.fetchObjectOutputSettings(
      objectId,
      objectClassId,
    );
  }

  /**
   * Persist the export form's last-used values for the object. Owner method:
   * fire-and-forget POST, then the `…Success` broadcast `object.reducer` uses to
   * mirror the values onto the current object. Failure is silent by design.
   */
  saveObjectExportSettingsState(
    contextObject: DataObject,
    settingsType: OutputSettingsType,
    settings: any,
  ): void {
    this.outputSettingsApi
      .saveObjectExportSettingsState(contextObject, settingsType, settings)
      .subscribe({
        next: () =>
          this.store.dispatch(
            saveObjectOutputSettingsStateSuccess({
              objectId: contextObject.id,
              objectClassId: contextObject.object_class_id,
              settingsType,
              settings,
            }),
          ),
        error: (error) =>
          console.error('Failed to persist export settings state', error),
      });
  }
}
